import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import useTranslations from "@/i18n/useTranslations";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import XIcon from "@/shared-components/icons/components/public/XIcon";
import PublicFormInput from "@/shared-components/inputs/components/PublicFormInput";
import GenericPopup from "@/shared-components/popups/components/GenericPopup";
import { PAYMENT_PROFILE_COUNTRIES, resolvePaymentCountryCode } from "@/utils/paymentCountry.utils";

import "./styles/PaymentProfileModal.css";

const PAYMENT_ELEMENT_OPTIONS = {
  layout: "tabs",
  paymentMethodOrder: ["card"],
  wallets: {
    applePay: "never",
    googlePay: "never",
    link: "never",
  },
  fields: {
    billingDetails: {
      name: "auto",
      email: "auto",
      phone: "auto",
      address: "if_required",
    },
  },
};

function deriveDefaultCountry(authorization, fallback) {
  const candidate = authorization?.metadata?.auction_bid_country
    ?? authorization?.metadata?.country
    ?? fallback;
  return resolvePaymentCountryCode(candidate) ?? "";
}

function trimText(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : "";
  }
  return "";
}

function resolveStripeThemeAppearance() {
  if (typeof window === "undefined") {
    return null;
  }

  const rootStyles = window.getComputedStyle(document.documentElement);
  const backgroundColor = rootStyles.getPropertyValue("--app-background-color").trim() || "#000000";
  const textColor = rootStyles.getPropertyValue("--app-text-color").trim() || "#ffffff";
  const greyColor = rootStyles.getPropertyValue("--app-grey-color").trim() || "#8f909c";
  const themeColor = rootStyles.getPropertyValue("--app-theme-color").trim() || textColor;

  return {
    theme: "flat",
    variables: {
      borderRadius: "12px",
      colorPrimary: themeColor,
      colorBackground: backgroundColor,
      colorText: textColor,
      colorTextSecondary: greyColor,
      colorDanger: "#ff9898",
      fontFamily: "Satoshi, sans-serif",
      spacingUnit: "4px",
    },
    rules: {
      ".Label": {
        color: greyColor,
      },
      ".Input": {
        backgroundColor,
        color: textColor,
        boxShadow: "none",
      },
      ".Input::placeholder": {
        color: greyColor,
      },
      ".Input:focus": {
        backgroundColor,
        color: textColor,
        boxShadow: "none",
      },
      ".Tab": {
        color: greyColor,
        backgroundColor,
        boxShadow: "none",
      },
      ".Tab--selected": {
        color: textColor,
        backgroundColor,
        boxShadow: "none",
      },
      ".TermsText": {
        color: greyColor,
      },
    },
  };
}

function PaymentProfileForm({
  mode,
  country,
  cardholderName,
  onCardholderNameChange,
  onSubmit,
  onError,
  submitting,
}) {
  const t = useTranslations("common.shared.artwork_view.payment_profile_modal");
  const stripe = useStripe();
  const elements = useElements();

  const handleCardholderChange = useCallback((event) => {
    onCardholderNameChange?.(event.target.value);
  }, [onCardholderNameChange]);

  const confirmIntent = useCallback(async () => {
    if (!stripe || !elements) {
      throw new Error(t("errors.stripe_not_ready"));
    }

    const trimmedCardholder = typeof cardholderName === "string"
      ? cardholderName.trim()
      : "";

    const confirmParams = {
      payment_method_data: {
        billing_details: {
          name: trimmedCardholder || undefined,
          address: {
            country,
          },
        },
      },
    };

    const options = {
      elements,
      confirmParams,
      redirect: "if_required",
    };

    if (mode === "authorization") {
      return stripe.confirmPayment(options);
    }

    return stripe.confirmSetup(options);
  }, [cardholderName, country, elements, mode, stripe, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    try {
      const intentResult = await confirmIntent();

      if (intentResult?.error) {
        throw intentResult.error;
      }

      const intent = mode === "authorization"
        ? intentResult?.paymentIntent ?? null
        : intentResult?.setupIntent ?? null;

      const paymentMethodId = intent?.payment_method ?? null;

      await onSubmit({
        paymentMethodId,
        intent,
        country,
        mode,
      });
    }
    catch (error) {
      onError?.(error);
    }
  }, [confirmIntent, country, mode, onError, onSubmit, t]);

  return (
    <form className="payment-profile-modal__form" onSubmit={handleSubmit}>
      <div className="payment-profile-modal__payment-wrapper">
        <div className="payment-profile-modal__details">
          <div className="payment-profile-modal__field">
            <label className="payment-profile-modal__label" htmlFor="payment-profile-cardholder">
              {t("form.cardholder_name")}
            </label>
            <input
              id="payment-profile-cardholder"
              type="text"
              className="payment-profile-modal__input"
              placeholder={t("form.cardholder_placeholder")}
              autoComplete="cc-name"
              value={cardholderName}
              onChange={handleCardholderChange}
              required
            />
          </div>
        </div>

        <PaymentElement
          id="payment-profile-payment"
          className="payment-profile-modal__payment-element"
          options={PAYMENT_ELEMENT_OPTIONS}
        />
      </div>

      {(submitting || !stripe || !elements) && (
        <p className="payment-profile-modal__helper payment-profile-modal__helper--muted">
          {t("form.connecting")}
        </p>
      )}

      <div className="payment-profile-modal__actions">
        <ContinueButton
          type="submit"
          className="payment-profile-modal__submit"
          disabled={submitting || !stripe || !elements}
        >
          {mode === "authorization" ? t("actions.confirm_payment_method") : t("actions.save_payment_method")}
        </ContinueButton>
      </div>
    </form>
  );
}

PaymentProfileForm.propTypes = {
  country: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["setup", "authorization"]).isRequired,
  onError: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
};

function PaymentProfileModalRenderer({
  open,
  mode,
  stripePromise,
  authorization,
  defaultCountry,
  pendingBid,
  userName = "",
  initialProfile = null,
  onClose,
  onCancel,
  onSubmit,
  onPrepareSetupIntent,
  onSuccess,
}) {
  const t = useTranslations("common.shared.artwork_view.payment_profile_modal");
  const [country, setCountry] = useState(() => deriveDefaultCountry(authorization, defaultCountry));
  const [cardholderName, setCardholderName] = useState(() => trimText(
    initialProfile?.cardholder_name ?? userName ?? ""
  ));
  const handleClose = useCallback(() => {
    onCancel?.();
    onClose?.();
  }, [onCancel, onClose]);
  const [step, setStep] = useState("collect-country");
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoadingIntent, setIsLoadingIntent] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [elementsKey, setElementsKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoCountryHandled, setAutoCountryHandled] = useState(false);
  const [stripeAppearance, setStripeAppearance] = useState(() => resolveStripeThemeAppearance());
  const hasInitialisedRef = useRef(false);

  const hasStoredCountry = Boolean(
    initialProfile?.country ??
    initialProfile?.country_iso ??
    initialProfile?.country_code ??
    null
  );

  const selectedCountryOption = useMemo(() => {
    if (!country) {
      return null;
    }
    return PAYMENT_PROFILE_COUNTRIES.find((option) => option.isoCode === country) ?? null;
  }, [country]);

  const countryDisplayName = selectedCountryOption?.name ?? (country ?? "");

  const handleCountrySelect = useCallback((selectedName) => {
    const option = PAYMENT_PROFILE_COUNTRIES.find((item) => item.name === selectedName);
    const isoCode = option?.isoCode ?? "";
    setCountry(isoCode);
  }, []);

  const handleCountryReset = useCallback(() => {
    setStep("collect-country");
    setClientSecret(null);
    setSubmitError(null);
    setIsLoadingIntent(false);
    setAutoCountryHandled(true);
  }, []);

  useEffect(() => {
    if (!open) {
      hasInitialisedRef.current = false;
      return;
    }

    // Avoid resetting the modal while it's open when the user/me refreshes
    // (which updates initialProfile) by only initialising on the first open.
    if (hasInitialisedRef.current) {
      return;
    }

    hasInitialisedRef.current = true;
    const nextDefaultCountry = deriveDefaultCountry(authorization, defaultCountry);
    setCountry(nextDefaultCountry);
    setStep("collect-country");
    setClientSecret(null);
    setSubmitError(null);
    setElementsKey((key) => key + 1);
    setIsSubmitting(false);
    setCardholderName(trimText(initialProfile?.cardholder_name ?? userName ?? ""));
    setAutoCountryHandled(false);
  }, [authorization, defaultCountry, initialProfile, open, userName]);

  useEffect(() => {
    if (!open || typeof window === "undefined") {
      return undefined;
    }

    const applyAppearance = () => {
      setStripeAppearance(resolveStripeThemeAppearance());
      setElementsKey((key) => key + 1);
    };

    applyAppearance();

    const observer = new MutationObserver((mutations) => {
      const modeChanged = mutations.some(
        (mutation) => mutation.type === "attributes" && mutation.attributeName === "data-mode"
      );
      if (modeChanged) {
        applyAppearance();
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-mode"],
    });

    return () => {
      observer.disconnect();
    };
  }, [open]);

  useEffect(() => {
    if (mode === "authorization" && authorization?.stripe_client_secret && step === "collect-country" && open) {
      setClientSecret(authorization.stripe_client_secret);
    }
  }, [authorization, mode, open, step]);

  const startVerification = useCallback(async () => {
    if (!country || !country.trim()) {
      setSubmitError(t("errors.select_country"));
      return;
    }

    setSubmitError(null);

    if (mode === "authorization") {
      setStep("collect-payment");
      setElementsKey((key) => key + 1);
      return;
    }

    setIsLoadingIntent(true);
    try {
      const { response, data } = await onPrepareSetupIntent({
        country,
      });

      if (!response?.ok) {
        const message = data?.error_message ?? t("errors.unable_to_prepare_verification");
        throw new Error(message);
      }

      if (!data?.client_secret) {
        throw new Error(t("errors.missing_client_secret"));
      }

      setClientSecret(data.client_secret);
      setStep("collect-payment");
      setElementsKey((key) => key + 1);
    }
    catch (error) {
      const message = error?.message ?? t("errors.unable_to_prepare_verification");
      setSubmitError(message);
    }
    finally {
      setIsLoadingIntent(false);
    }
  }, [country, mode, onPrepareSetupIntent, t]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!country || !hasStoredCountry) {
      return;
    }
    if (step !== "collect-country" || autoCountryHandled || isLoadingIntent) {
      return;
    }
    setAutoCountryHandled(true);
    if (mode === "authorization") {
      setStep("collect-payment");
      setElementsKey((key) => key + 1);
      return;
    }
    startVerification();
  }, [
    autoCountryHandled,
    country,
    hasStoredCountry,
    isLoadingIntent,
    mode,
    open,
    startVerification,
    step,
  ]);

  const handleStripeSubmission = useCallback(async (payload) => {
    setSubmitError(null);
    const trimmedCardholder = trimText(cardholderName);

    if (!trimmedCardholder) {
      const message = t("errors.add_cardholder_name");
      setSubmitError(message);
      throw new Error(message);
    }

    setIsSubmitting(true);
    try {
      const result = await onSubmit({
        ...payload,
        mode: payload.mode ?? mode,
        country,
        cardholderName: trimmedCardholder,
        pendingBid: payload.pendingBid ?? pendingBid ?? null,
      });
      onSuccess?.(result);
    }
    catch (error) {
      const message = error?.message ?? t("errors.unable_to_store");
      setSubmitError(message);
      throw error;
    }
    finally {
      setIsSubmitting(false);
    }
  }, [cardholderName, country, mode, onSubmit, onSuccess, pendingBid, t]);

  const elementsOptions = useMemo(() => {
    if (!clientSecret || !stripeAppearance) {
      return null;
    }
    return {
      clientSecret,
      appearance: stripeAppearance,
    };
  }, [clientSecret, stripeAppearance]);

  const renderCountryStep = (
    <div className="payment-profile-modal__step">
      <PublicFormInput
        className="payment-profile-modal__select"
        type="select"
        placeholder={t("form.select_country_placeholder")}
        element="payment-profile-country"
        value={countryDisplayName}
        onChange={handleCountrySelect}
        selectOptions={{ options: PAYMENT_PROFILE_COUNTRIES, type: "country" }}
      />

      {submitError && (
        <p className="payment-profile-modal__error" role="alert">
          {submitError}
        </p>
      )}

      <div className="payment-profile-modal__actions">
        <ContinueButton
          className="payment-profile-modal__submit"
          onClick={startVerification}
          disabled={isLoadingIntent}
        >
          {t("actions.continue")}
        </ContinueButton>
      </div>
    </div>
  );

  const renderPaymentStep = clientSecret && elementsOptions && stripePromise && (
    <>
      {countryDisplayName && (
        <div className="payment-profile-modal__country-note">
          {t("country_note.prefix")} <strong>{countryDisplayName}</strong>.
          <button
            type="button"
            className="payment-profile-modal__link"
            onClick={handleCountryReset}
          >
            {t("country_note.change_country")}
          </button>
        </div>
      )}
      <Elements
        stripe={stripePromise}
        options={elementsOptions}
        key={elementsKey}
      >
        <PaymentProfileForm
          mode={mode}
          country={country}
          cardholderName={cardholderName}
          onCardholderNameChange={setCardholderName}
          submitting={isSubmitting}
          onSubmit={handleStripeSubmission}
          onError={(error) => setSubmitError(error?.message ?? t("errors.unable_to_verify"))}
        />
      </Elements>
    </>
  );

  return (
    <GenericPopup
      open={open}
      className="payment-profile-modal__container"
      backgroundClassName="payment-profile-modal__overlay"
    >
      <header className="payment-profile-modal__header">
        <div className="payment-profile-modal__header-content">
          <div className="payment-profile-modal__title-row">
            <h2 className="payment-profile-modal__title">
              {mode === "authorization" ? t("titles.verify_payment_method") : t("titles.add_payment_method")}
            </h2>
            <XIcon className="generic-popup__close" onClick={handleClose} />
          </div>
          <p className="payment-profile-modal__subtitle">
            {t("subtitle")}
          </p>
        </div>
      </header>

      {step === "collect-country" ? renderCountryStep : renderPaymentStep}

      {submitError && step === "collect-payment" && (
        <p className="payment-profile-modal__error" role="alert">
          {submitError}
        </p>
      )}
    </GenericPopup>
  );
}

export default function PaymentProfileModal(props) {
  if (!props.open) {
    return null;
  }
  return <PaymentProfileModalRenderer {...props} />;
}

PaymentProfileModal.propTypes = {
  authorization: PropTypes.object,
  defaultCountry: PropTypes.string,
  initialProfile: PropTypes.object,
  mode: PropTypes.oneOf(["setup", "authorization"]),
  pendingBid: PropTypes.object,
  userName: PropTypes.string,
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  onPrepareSetupIntent: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  open: PropTypes.bool,
  stripePromise: PropTypes.object,
};
